/**
 * TicketFiles - a ticket's Files section: images shown, the rest downloaded,
 * and (Tickets Edit) an "Add files" zone that takes a browse, a drop or a
 * Ctrl+V paste while the ticket is open. A ticket imported from email has
 * its screenshots only in Outlook; this is how they get into GlobalOps.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { DropNotice } from '../../components/shared/attachmentsPanel/panelParts';
import { acceptDrag, resolveSnapshot, snapshotTransfer } from '../../utils/droppedFiles';
import { addTicketFiles, errorOf, fileBlobUrl, removeTicketFile } from './ticketsApi';

const label = 'block text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-1';

/** The Outlook link of an imported ticket whose files stayed in the email, else null. */
export function outlookLinkOf(t) {
  if (!t || t.source !== 'email_import') return null;
  const note = String(t.source_note || '');
  if (!/attachments are in the original email/i.test(note)) return null;
  const m = /https?:\/\/\S+/.exec(note);
  return m ? m[0] : null;
}

function FileItem({ file, onRemove }) {
  const isImage = /^image\//.test(file.mime || '');
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!isImage) return undefined;
    let url = null;
    let live = true;
    fileBlobUrl(file.url, true).then((u) => {
      url = u;
      if (live) setThumb(u);
      else if (u) URL.revokeObjectURL(u);
    });
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file.url, isImage]);

  const download = async () => {
    const url = await fileBlobUrl(file.url, false);
    if (!url) return alert('Could not download the file.');
    const a = document.createElement('a');
    a.href = url;
    a.download = file.filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return undefined;
  };

  return (
    <li className="border border-border-strong rounded p-2">
      {isImage && thumb && (
        <a href={thumb} target="_blank" rel="noreferrer">
          <img src={thumb} alt={file.filename} className="max-h-56 max-w-full rounded mb-1.5" />
        </a>
      )}
      <div className="flex items-center gap-2 text-[12px]">
        <span className="flex-1 truncate">{file.filename}</span>
        <span className="text-text-muted">{((file.size || 0) / 1024).toFixed(0)} KB</span>
        <Button size="sm" variant="secondary" onClick={download}>
          Download
        </Button>
        {onRemove && (
          <Button size="sm" variant="secondary" onClick={() => onRemove(file)} aria-label={`Remove ${file.filename}`}>
            Remove
          </Button>
        )}
      </div>
    </li>
  );
}

export default function TicketFiles({ ticket, editable, onChanged }) {
  const [files, setFiles] = useState(ticket.files || []);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [over, setOver] = useState(false);
  const rootRef = useRef(null);
  const pickRef = useRef(null);
  useEffect(() => setFiles(ticket.files || []), [ticket.files]);

  const send = async (list) => {
    if (!list.length) return;
    setBusy(true);
    setErr(null);
    const r = await addTicketFiles(ticket.id, list);
    setBusy(false);
    if (!r.ok) return setErr(errorOf(r, 'Could not add the files.'));
    setFiles((cur) => [...cur, ...((r.body && r.body.files) || [])]);
    if (onChanged) onChanged();
    return undefined;
  };

  /** The DataTransfer is only readable during the event: snapshot now, fetch links after. */
  const take = async (dt, opts) => {
    const snap = snapshotTransfer(dt, opts);
    if (snap.files.length) {
      setNotice(null);
      return send(snap.files);
    }
    if (!snap.urls.length) {
      if (snap.hadData && !(opts && opts.quietIfEmpty)) setNotice({ kind: 'none' });
      return undefined;
    }
    setNotice({ kind: 'busy' });
    const { files: got, links } = await resolveSnapshot(snap);
    setNotice(links.length ? { kind: 'links', links } : null);
    return send(got);
  };
  const takeRef = useRef(take);
  takeRef.current = take;

  useEffect(() => {
    if (!editable) return undefined;
    const onPaste = (e) => {
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      // another window on top (Submit a ticket ...) owns the paste
      const dialogs = document.querySelectorAll('[role="dialog"]');
      const top = dialogs.length ? dialogs[dialogs.length - 1] : null;
      if (top && !top.contains(rootRef.current)) return;
      const cd = e.clipboardData;
      if (!cd) return;
      const hasFile = Array.from(cd.items || []).some((it) => it && it.kind === 'file') || (cd.files && cd.files.length > 0);
      const text = typeof cd.getData === 'function' ? cd.getData('text/plain') || '' : '';
      if (!hasFile && !/^\s*(https?:|data:)\S+\s*$/i.test(text)) return;
      e.preventDefault();
      takeRef.current(cd, { renameGeneric: true, quietIfEmpty: true });
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [editable]);

  const remove = async (file) => {
    if (!window.confirm(`Remove ${file.filename} from this ticket?`)) return undefined;
    const r = await removeTicketFile(ticket.id, file.id);
    if (!r.ok) return setErr(errorOf(r, 'Could not remove the file.'));
    setFiles((cur) => cur.filter((f) => f.id !== file.id));
    if (onChanged) onChanged();
    return undefined;
  };

  if (!editable && !files.length) return null;
  const outlook = editable ? outlookLinkOf(ticket) : null;

  return (
    <div ref={rootRef}>
      <div className={label}>Files</div>
      {files.length > 0 && (
        <ul className="space-y-2 mb-2">
          {files.map((f) => (
            <FileItem key={f.id} file={f} onRemove={editable ? remove : null} />
          ))}
        </ul>
      )}
      {editable && (
        <>
          {outlook && (
            <div className="mb-1.5 text-text-muted" data-testid="outlook-hint">
              Drag the screenshot from the original email (
              <a href={outlook} target="_blank" rel="noreferrer" className="text-gat-green font-semibold hover:underline">
                Open in Outlook
              </a>
              ) into here.
            </div>
          )}
          <DropNotice notice={notice} onClose={() => setNotice(null)} />
          <div
            data-testid="ticket-drop-zone"
            className={`border-2 border-dashed rounded p-3 text-center ${over ? 'border-gat-green bg-surface-2' : 'border-border-strong'}`}
            onDragOver={(e) => {
              acceptDrag(e);
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              take(e.dataTransfer);
            }}
          >
            <div className="text-text-muted mb-1.5">{busy ? 'Adding...' : 'Drop files here or paste a screenshot (Ctrl+V)'}</div>
            <Button size="sm" variant="secondary" onClick={() => pickRef.current && pickRef.current.click()} disabled={busy}>
              Add files
            </Button>
            <input
              ref={pickRef}
              type="file"
              multiple
              hidden
              aria-label="Add files"
              onChange={(e) => {
                const list = Array.from(e.target.files || []);
                e.target.value = '';
                send(list);
              }}
            />
          </div>
          {err && <div className="mt-1 text-status-lost">{err}</div>}
        </>
      )}
    </div>
  );
}
